import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { CreditCard, Upload, Save, AlertCircle, CheckCircle, Image as ImageIcon } from 'lucide-react';

interface PaymentSettings {
  promptpay: {
    id: string;
    name: string;
    qrCodeUrl?: string;
  };
  bank: {
    bankName: string;
    accountNumber: string;
    accountName: string;
  };
  updatedAt?: any;
}

export default function SalepagePaymentSettings() {
  const [settings, setSettings] = useState<PaymentSettings>({
    promptpay: {
      id: '',
      name: '',
      qrCodeUrl: ''
    },
    bank: {
      bankName: '',
      accountNumber: '',
      accountName: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [qrPreview, setQrPreview] = useState<string>('');
  const [qrBase64, setQrBase64] = useState<string>('');
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'salePageSettings', 'payment'));
      if (settingsDoc.exists()) {
        setSettings(settingsDoc.data() as PaymentSettings);
        if (settingsDoc.data().promptpay?.qrCodeUrl) {
          setQrPreview(settingsDoc.data().promptpay.qrCodeUrl);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading payment settings:', error);
      setLoading(false);
    }
  };

  const handleQrFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'ไฟล์ใหญ่เกินไป (สูงสุด 2MB)' });
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setQrPreview(base64String);
        setQrBase64(base64String);
      };
      reader.readAsDataURL(file);
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setSaving(true);

    try {
      // Use base64 if new image was uploaded, otherwise keep existing
      const qrCodeUrl = qrBase64 || settings.promptpay.qrCodeUrl || '';

      const updatedSettings: PaymentSettings = {
        promptpay: {
          ...settings.promptpay,
          qrCodeUrl
        },
        bank: settings.bank,
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, 'salePageSettings', 'payment'), updatedSettings);
      
      setSettings(updatedSettings);
      setQrBase64('');
      setMessage({ type: 'success', text: 'บันทึกการตั้งค่าสำเร็จ' });
      
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving payment settings:', error);
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาดในการบันทึก' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <CreditCard className="w-7 h-7 text-emerald-600" />
          ตั้งค่าการรับชำระเงิน
        </h2>
        <p className="text-gray-600 mt-1">จัดการข้อมูลบัญชีและ QR Code สำหรับรับชำระเงิน Salepage</p>
      </div>

      {message && (
        <div className={`flex items-center gap-2 p-4 rounded-xl ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-700' 
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* PromptPay Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">PromptPay</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                PromptPay ID
              </label>
              <input
                type="text"
                value={settings.promptpay.id}
                onChange={(e) => setSettings({
                  ...settings,
                  promptpay: { ...settings.promptpay, id: e.target.value }
                })}
                placeholder="เช่น 0XX-XXX-XXXX"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                ชื่อบัญชี
              </label>
              <input
                type="text"
                value={settings.promptpay.name}
                onChange={(e) => setSettings({
                  ...settings,
                  promptpay: { ...settings.promptpay, name: e.target.value }
                })}
                placeholder="เช่น EduMi Platform"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                QR Code สำหรับสแกนจ่าย
              </label>
              
              {qrPreview && (
                <div className="mb-4 p-4 bg-gray-50 rounded-xl">
                  <img 
                    src={qrPreview} 
                    alt="QR Code Preview" 
                    className="w-48 h-48 object-contain mx-auto border-2 border-gray-200 rounded-lg"
                  />
                </div>
              )}

              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  {qrPreview ? 'เปลี่ยน QR Code' : 'อัปโหลด QR Code'}
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  รองรับไฟล์ JPG, PNG (ขนาดไม่เกิน 2MB)
                </p>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleQrFileChange}
                  className="hidden"
                  id="qr-upload"
                />
                <label
                  htmlFor="qr-upload"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 cursor-pointer transition-all"
                >
                  <Upload className="w-4 h-4" />
                  เลือกไฟล์
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Bank Transfer Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">โอนเงินผ่านธนาคาร</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                ธนาคาร
              </label>
              <input
                type="text"
                value={settings.bank.bankName}
                onChange={(e) => setSettings({
                  ...settings,
                  bank: { ...settings.bank, bankName: e.target.value }
                })}
                placeholder="เช่น ธนาคารกสิกรไทย"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                เลขที่บัญชี
              </label>
              <input
                type="text"
                value={settings.bank.accountNumber}
                onChange={(e) => setSettings({
                  ...settings,
                  bank: { ...settings.bank, accountNumber: e.target.value }
                })}
                placeholder="เช่น XXX-X-XXXXX-X"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                ชื่อบัญชี
              </label>
              <input
                type="text"
                value={settings.bank.accountName}
                onChange={(e) => setSettings({
                  ...settings,
                  bank: { ...settings.bank, accountName: e.target.value }
                })}
                placeholder="เช่น EduMi Platform"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none"
                required
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full px-6 py-4 bg-gradient-to-r from-emerald-600 to-cyan-600 text-white rounded-xl font-bold text-lg hover:from-emerald-700 hover:to-cyan-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              กำลังบันทึก...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              บันทึกการตั้งค่า
            </>
          )}
        </button>
      </form>
    </div>
  );
}
